/**
 * Sounds that are not sim events but edges and rhythms in sim STATE: the jump buzzer when the
 * light goes green, a time bomb's accelerating tick (from `fuseTicks`), hard breathing, and the
 * three seconds of rustle while a wound is dressed; and (ROUND8) a dog's growl while it runs his trail toward him.
 */

import type { World } from '../../sim/types';
import { Growls } from '../dogVoice';
import { bombTicked, bombTickRate, breathSeconds } from '../mix';
import { barkRate } from '../sfx';
import type { Voices } from './voices';

export class Pulses {
  private greenLight = false;
  private action: World['ranger']['action'] = 'none';
  private readonly fuses = new Map<number, number>();
  private breathClock = 0;
  private breathingOut = false;
  private readonly growls = new Growls();

  constructor(private readonly voices: Voices) {}

  begin(): void {
    this.greenLight = false;
    this.action = 'none';
    this.fuses.clear();
    this.breathClock = 0;
    this.growls.reset();
  }

  update(world: World, dt: number): void {
    const ins = world.insertion;
    // The buzzer (01 §10): the rising edge of the green light, while still aboard.
    if (ins.greenLight && !this.greenLight && ins.phase === 'flyover') this.voices.playUi('jumpBuzzer');
    this.greenLight = ins.greenLight;

    const r = world.ranger;
    if (r.action === 'firstAid' && this.action !== 'firstAid') this.voices.playAt('firstAid', r.x, r.z);
    this.action = r.action;

    for (const bomb of world.bombs) {
      const before = this.fuses.get(bomb.id);
      this.fuses.set(bomb.id, bomb.fuseTicks);
      if (before !== undefined && bombTicked(before, bomb.fuseTicks)) {
        this.voices.playAt('bombTick', bomb.x, bomb.z, 1, bombTickRate(bomb.fuseTicks));
      }
    }
    if (world.bombs.length === 0 && this.fuses.size > 0) this.fuses.clear();
    // Each dog growls in its own voice, the pitch its barks have.
    this.growls.step(world.enemies, r.x, r.z, dt, (dog) => {
      this.voices.playAt('dogGrowl', dog.x, dog.z, 1, barkRate(dog.id));
    });

    const period = ins.phase === 'ground' ? breathSeconds(r) : 0;
    if (period <= 0) {
      this.breathClock = 0;
      return;
    }
    this.breathClock += dt;
    if (this.breathClock >= period / 2) {
      this.breathClock = 0;
      this.voices.playAt(this.breathingOut ? 'breathOut' : 'breathIn', r.x, r.z);
      this.breathingOut = !this.breathingOut;
    }
  }
}
